import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api';

const OrderDetails = ({ order, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    order_date: order.order_date || '',
    point_number: order.point_number || '',
    counterparty_id: order.counterparty_id || '',
    route_id: order.route_id || '',
    driver_id: order.driver_id || '',
    car_id: order.car_id || '',
    price: order.price || '',
    weight_kg: order.weight_kg || '',
    boxes_count: order.boxes_count || '',
    volume_m3: order.volume_m3 || '',
    status: order.status || 'new',
    notes: order.notes || ''
  });

  const [counterparties, setCounterparties] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const [cp, r, d, c] = await Promise.all([
        axios.get(`${API_URL}/counterparties`),
        axios.get(`${API_URL}/routes`),
        axios.get(`${API_URL}/drivers`),
        axios.get(`${API_URL}/cars`)
      ]);
      setCounterparties(cp.data || []);
      setRoutes(r.data || []);
      setDrivers(d.data || []);
      setCars(c.data || []);
    } catch (err) {
      console.error('Ошибка загрузки списков:', err);
    }
  };

  const getName = (list, id) => {
    if (!list || !list.length) return '—';
    const item = list.find(x => x.id === id);
    return item ? item.name : '—';
  };

  const getCarName = (id) => {
    if (!cars || !cars.length) return '—';
    const item = cars.find(x => x.id === id);
    return item ? `${item.model} (${item.plate_number})` : '—';
  };

  const getStatusText = (status) => {
    const map = {
      'new': '🆕 Новая',
      'in_progress': '🚗 В пути',
      'completed': '✅ Завершена',
      'cancelled': '❌ Отменена'
    };
    return map[status] || status;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const data = {
        order_date: formData.order_date,
        point_number: formData.point_number,
        counterparty_id: parseInt(formData.counterparty_id),
        route_id: parseInt(formData.route_id) || null,
        driver_id: parseInt(formData.driver_id),
        car_id: parseInt(formData.car_id),
        price: formData.price ? parseInt(formData.price) : null,
        weight_kg: formData.weight_kg ? parseInt(formData.weight_kg) : null,
        boxes_count: formData.boxes_count ? parseInt(formData.boxes_count) : null,
        volume_m3: formData.volume_m3 ? parseFloat(formData.volume_m3) : null,
        status: formData.status,
        notes: formData.notes
      };
      await axios.put(`${API_URL}/orders/${order.id}`, data);
      alert('✅ Заявка обновлена!');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError('Ошибка: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const displayRow = (label, value) => (
    <tr>
      <td style={styles.label}>{label}</td>
      <td style={styles.value}>{value || '—'}</td>
    </tr>
  );

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.header}>
          <h2 style={{margin: 0}}>📋 Заявка №{order.id}</h2>
          <button onClick={onClose} style={styles.btnClose}>✖</button>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {!editing ? (
          <div>
            <table style={styles.table}>
              <tbody>
                {displayRow('📅 Дата', formData.order_date)}
                {displayRow('🎯 Номер точки', formData.point_number)}
                {displayRow('🏢 Контрагент', getName(counterparties, parseInt(formData.counterparty_id)))}
                {displayRow('🗺️ Маршрут', getName(routes, parseInt(formData.route_id)))}
                {displayRow('👨 Водитель', getName(drivers, parseInt(formData.driver_id)))}
                {displayRow('🚗 Автомобиль', getCarName(parseInt(formData.car_id)))}
                {displayRow('💰 Цена', formData.price ? `${formData.price} ₽` : '—')}
                {displayRow('⚖️ Вес, кг', formData.weight_kg)}
                {displayRow('📦 Короба', formData.boxes_count)}
                {displayRow('📐 Объём, м³', formData.volume_m3)}
                {displayRow('⚙️ Статус', getStatusText(formData.status))}
              </tbody>
            </table>

            <div style={styles.notesSection}>
              <h3 style={{margin: '0 0 10px 0'}}>📝 Заметки / Комментарий:</h3>
              <pre style={styles.notesText}>{formData.notes || 'Нет заметок'}</pre>
            </div>

            <div style={styles.buttons}>
              <button onClick={() => setEditing(true)} style={styles.btnEdit}>
                ✏️ Редактировать
              </button>
              <button onClick={onClose} style={styles.btnCancel}>
                Закрыть
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3>✏️ Редактирование заявки №{order.id}</h3>
            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>📅 Дата *</label>
                <input type="date" value={formData.order_date} onChange={(e) => handleChange('order_date', e.target.value)} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>🎯 Номер точки</label>
                <input type="text" value={formData.point_number} onChange={(e) => handleChange('point_number', e.target.value)} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>🏢 Контрагент *</label>
                <select value={formData.counterparty_id} onChange={(e) => handleChange('counterparty_id', e.target.value)} style={styles.input}>
                  <option value="">Выберите...</option>
                  {counterparties.map(cp => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>🗺️ Маршрут</label>
                <select value={formData.route_id} onChange={(e) => handleChange('route_id', e.target.value)} style={styles.input}>
                  <option value="">Без маршрута</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>👨 Водитель *</label>
                <select value={formData.driver_id} onChange={(e) => handleChange('driver_id', e.target.value)} style={styles.input}>
                  <option value="">Выберите...</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>🚗 Автомобиль *</label>
                <select value={formData.car_id} onChange={(e) => handleChange('car_id', e.target.value)} style={styles.input}>
                  <option value="">Выберите...</option>
                  {cars.map(c => <option key={c.id} value={c.id}>{c.model} ({c.plate_number})</option>)}
                </select>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroupHalf}>
                  <label style={styles.label}>💰 Цена</label>
                  <input type="number" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} style={styles.input} />
                </div>
                <div style={styles.formGroupHalf}>
                  <label style={styles.label}>⚙️ Статус</label>
                  <select value={formData.status} onChange={(e) => handleChange('status', e.target.value)} style={styles.input}>
                    <option value="new">🆕 Новая</option>
                    <option value="in_progress">🚗 В пути</option>
                    <option value="completed">✅ Завершена</option>
                    <option value="cancelled">❌ Отменена</option>
                  </select>
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroupHalf}>
                  <label style={styles.label}>⚖️ Вес, кг</label>
                  <input type="number" value={formData.weight_kg} onChange={(e) => handleChange('weight_kg', e.target.value)} style={styles.input} />
                </div>
                <div style={styles.formGroupHalf}>
                  <label style={styles.label}>📦 Короба</label>
                  <input type="number" value={formData.boxes_count} onChange={(e) => handleChange('boxes_count', e.target.value)} style={styles.input} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>📐 Объём, м³</label>
                <input type="number" step="0.01" value={formData.volume_m3} onChange={(e) => handleChange('volume_m3', e.target.value)} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>📝 Заметки</label>
                <textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} style={styles.textarea} rows="5" placeholder="Комментарии, адреса..." />
              </div>
            </div>

            <div style={styles.buttons}>
              <button onClick={handleSave} disabled={loading} style={styles.btnSave}>
                {loading ? 'Сохранение...' : '✅ Сохранить'}
              </button>
              <button onClick={() => setEditing(false)} style={styles.btnCancel}>
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
  },
  modalContent: {
    background: '#fff', padding: '25px', borderRadius: '10px', width: '650px', maxWidth: '95%',
    maxHeight: '90vh', overflowY: 'auto'
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
  btnClose: {
    background: '#ccc', border: 'none', borderRadius: '50%', width: '30px', height: '30px',
    cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '15px' },
  label: { padding: '8px 10px', fontWeight: 'bold', background: '#f5f5f5', width: '35%', borderBottom: '1px solid #eee' },
  value: { padding: '8px 10px', borderBottom: '1px solid #eee' },
  notesSection: {
    background: '#fff3cd', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #ffc107', marginBottom: '15px'
  },
  notesText: {
    whiteSpace: 'pre-wrap', margin: 0, fontSize: '13px', fontFamily: 'inherit', lineHeight: '1.5'
  },
  buttons: { display: 'flex', gap: '10px' },
  btnEdit: {
    flex: 1, padding: '12px', background: '#0C7281', color: '#fff', border: 'none',
    borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'
  },
  btnSave: {
    flex: 1, padding: '12px', background: '#075A64', color: '#fff', border: 'none',
    borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'
  },
  btnCancel: {
    flex: 1, padding: '12px', background: '#ccc', border: 'none', borderRadius: '5px',
    cursor: 'pointer', fontWeight: 'bold'
  },
  errorBox: { background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '5px', marginBottom: '15px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  formRow: { display: 'flex', gap: '10px' },
  formGroupHalf: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  label: { fontWeight: 'bold', fontSize: '13px', color: '#333' },
  input: { padding: '8px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '14px' },
  textarea: { padding: '8px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' },
};

export default OrderDetails;
